import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { DocEditor } from "../DocEditor";
import { SignaturePad } from "../SignaturePad";
import { UserSelect } from "./LegalPickers";
import { RETURN_REASONS, errMsg } from "./legalUtils";

const CFG = {
  escalate: ["تصعيد الحالة لأعلى", "اختر الشخص الذي تُصعّد إليه — سيرى كل إجراءاتك السابقة، وتُقفل محاضرك."],
  return: ["إرجاع الحالة", "أرجعها للمرحلة السابقة (أو لشخص تختاره) للاستعلام أو التغيير أو الإضافة."],
  close: ["إصدار القرار وإقفال الحالة", "تُقفل الحالة وتنتقل لأرشيف القضايا، ويُشعر كل من شارك فيها."],
  sign: ["التوقيع على ملف الحالة", "يظهر توقيعك في ملف PDF الخاص بالحالة."],
};

export const LegalActionDialog = ({ action, c, meta, onClose, onDone }) => {
  const [f, setF] = useState({ to_user_id: "", note: "", due_days: c.stage_days || 3, reason: RETURN_REASONS[0], result: meta.results[0], category: c.category });
  const [busy, setBusy] = useState(false);
  const ed = useRef(null), pad = useRef(null);
  const go = async () => {
    let body = { ...f, due_days: Number(f.due_days) || 3 };
    if (action === "escalate" && !f.to_user_id) { toast.error("اختر الشخص"); return; }
    if (action === "close") body = { result: f.result, category: f.category, decision_html: ed.current?.getHTML() || "" };
    if (action === "sign") { const s = pad.current?.getSignature(); if (!s) { toast.error("وقّع أولاً"); return; } body = { signature: s }; }
    setBusy(true);
    try { await api.legalAct(c.id, action, body); toast.success("تم"); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const [title, desc] = CFG[action];
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent dir="rtl" className={action === "close" ? "max-w-3xl max-h-[92vh] overflow-y-auto" : "max-w-md"} data-testid={`legal-dialog-${action}`}>
        <DialogHeader className="text-right"><DialogTitle className="font-heading">{title}</DialogTitle><DialogDescription>{desc}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          {(action === "escalate" || action === "return") && <>
            <label className="text-xs font-semibold text-slate-500">{action === "escalate" ? "التصعيد إلى" : "الإرجاع إلى (اتركه فارغاً للمرحلة السابقة)"}</label>
            <UserSelect users={meta.users} value={f.to_user_id} exclude={c.holder_id} onChange={(v) => setF({ ...f, to_user_id: v })} testId={`legal-${action}-to`} />
            {action === "return" && <div className="flex gap-1.5">{RETURN_REASONS.map((r) => <button key={r} onClick={() => setF({ ...f, reason: r })} data-testid={`legal-return-reason-${r}`} className={`flex-1 rounded-lg border py-1.5 text-xs font-bold ${f.reason === r ? "border-rose-500 bg-rose-50 text-rose-700" : "border-slate-200"}`}>{r}</button>)}</div>}
            <Textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} rows={3} placeholder="ملاحظاتك / المطلوب" data-testid={`legal-${action}-note`} />
            <div className="flex items-center gap-2 text-sm"><span>مهلة المرحلة التالية</span><Input type="number" min={1} max={90} value={f.due_days} onChange={(e) => setF({ ...f, due_days: e.target.value })} className="h-9 w-20" /><span>يوم</span></div>
          </>}
          {action === "close" && <>
            <div className="grid grid-cols-2 gap-2">
              <select value={f.result} onChange={(e) => setF({ ...f, result: e.target.value })} className="h-10 rounded-md border px-2 text-sm" data-testid="legal-close-result">{meta.results.map((r) => <option key={r}>{r}</option>)}</select>
              <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="h-10 rounded-md border px-2 text-sm" data-testid="legal-close-category">{meta.categories.map((r) => <option key={r}>{r}</option>)}</select>
            </div>
            <DocEditor ref={ed} compact initialHTML="" header={<p className="mb-2 text-center font-bold">نص القرار / الحكم</p>} />
          </>}
          {action === "sign" && <SignaturePad ref={pad} height={150} />}
          <Button onClick={go} disabled={busy} className="w-full bg-[#0f1f3d] hover:bg-[#1b3363]" data-testid={`legal-${action}-confirm`}>{busy && <Loader2 className="me-1 h-4 w-4 animate-spin" />}تأكيد</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
